// Related: [[knowledge/ui-components]] [[knowledge/bug-patterns]]
// Related: [[knowledge/raw/2026-05-26_phantom-nav-detector-approuter-keys]] (App-Router history.state keys + redirect allowlist)
import * as Sentry from '@sentry/nextjs';
import type {
  ClickTarget,
  HistoryStateDescriptor,
  InFlightRequest,
  PhantomNavConfidence,
  PhantomNavEvent,
  PhantomNavPattern,
} from './types';

type NavKind = 'pushState' | 'replaceState' | 'popstate' | 'click';

interface NavEvent {
  ts: number;
  kind: NavKind;
  fromUrl: string;
  toUrl: string;
  sinceLastClickMs: number;
  lastClickTarget?: ClickTarget;
  intendedUrl?: string | null;
  // Upgrade 5: history.state snapshots captured at push/replace time.
  historyStateFrom?: unknown;
  historyStateTo?: unknown;
  // Upgrade 7 (Fix A): stacked-ghost signals sampled at URL-settle time.
  // Stored on NavEvent so capturePhantomNav reads values from the settle moment,
  // not from the later capture moment when the rapid-nav storm may have resolved.
  // Optional because click events (kind === 'click') go through handleClick, not
  // recordNavigation, and have no URL-settle moment to sample from.
  rscInflightAtSettle?: number | null;
  staleSuspenseFallbackCount?: number | null;
  // Renamed from portalNodeCount: uses .children.length (elements only, no comment nodes).
  bodyElementCount?: number | null;
}

interface UrlParts {
  origin: string;
  pathname: string;
  search: string;
  hash: string;
}

interface InstallOptions {
  enabled?: boolean;
  queueSize?: number;
  snapBackWindowMs?: number;
  wrongDestWindowMs?: number;
  minSinceClickMs?: number;
}

/** Internal record kept in the RSC/server-action ring buffer (Upgrade 1). */
interface FetchRecord {
  url: string; // already sanitized at record time
  method: string;
  headerKind: 'rsc' | 'server-action';
  startTs: number; // performance.now()
  settleTs?: number; // undefined = still in flight
  status?: number;
  responseSizeBytes?: number;
  /** true when Next-Router-Prefetch: 1 header is present — background prefetch, not a
   *  user-triggered navigation. Prefetch RSC requests MUST NOT count toward the
   *  stacked-ghost trigger, which fires only on genuine navigation RSC fetches. */
  isPrefetch: boolean;
}

const DEFAULT_QUEUE_SIZE = 20;
// Snap-back window: must be wide enough to cover async server-action redirects
// (permission check on cold start can take 3-6s). The 2026-05-07 captured event
// fired at 4626ms after click. 8000ms covers realistic worst-case + buffer.
const DEFAULT_SNAP_BACK_WINDOW_MS = 8000;
const DEFAULT_WRONG_DEST_WINDOW_MS = 1500;
const DEFAULT_MIN_SINCE_CLICK_MS = 300;
const TAB_SNAP_WINDOW_MS = 1500;
const DEDUP_WINDOW_MS = 60_000;
const DEDUP_LIMIT = 5;
const SAFE_QUERY_KEYS = new Set(['tab', 'view', 'pane', 'step', 'page']);
// Filter-state URL syncs (q, sort, page) commonly fire on mount and would false-positive.
const TAB_SNAP_PARAM_KEYS = new Set(['tab', 'view', 'pane', 'step']);

// Known index→default-subpage config redirects from next.config.ts `redirects()`.
// When a user clicks an index route that the server 307/308-redirects to a canonical
// subpage, the detector sees toUrl(/settings/profile) !== intendedUrl(/settings) and
// would mis-fire the wrong-destination rule. These are BY DESIGN, not phantom-navs.
// Keys are the redirect SOURCE pathname; values are the redirect DESTINATION pathname.
// Source of truth: next.config.ts redirects(). Keep this list in sync with the index→
// default entries there (the *full-path-replacement* redirects, e.g. /settings →
// /settings/profile). Auth redirects (/login → /auth/login etc.) are also config
// redirects but originate from outside the dashboard click surface; included for
// completeness since they are equally by-design.
// IMPORTANT: keep this MINIMAL — only documented config redirects. Do NOT add entries to
// silence a genuine wrong-destination (a real router defect), which is exactly what this
// rule exists to catch.
// Exported for the next.config.ts parity regression test (drift guard, Rule #48).
export const KNOWN_CONFIG_REDIRECTS: Record<string, string> = {
  '/settings': '/settings/profile',
  '/login': '/auth/login',
  '/register': '/auth/register',
  '/forgot-password': '/auth/forgot-password',
  '/reset-password': '/auth/reset-password',
  // Instagram bio-link vanity redirect (2026-07-15) — parity with next.config.ts
  // redirects(); without this entry the watcher would flag every /ig visit as a
  // phantom nav. Value keeps the config's exact destination (parity test contract);
  // the runtime comparison below is pathname-level, so the query string is inert.
  '/ig': '/?utm_source=instagram&utm_medium=bio&ref=ig',
};

// Upgrade 1: RSC/server-action ring buffer constants.
const FETCH_RING_MAX = 30;
// Requests settled more than this many ms before the snap are excluded from correlation.
const FETCH_RECENT_SETTLE_WINDOW_MS = 2000;

let installed = false;
// Upgrade 1: flag so fetch is only patched once across hot-module reloads.
let fetchPatched = false;

/**
 * Reset all singleton state for unit testing. Call this in beforeEach/afterEach
 * to allow installPhantomNavWatcher to reinstall in each test.
 *
 * NEVER call this in production code — it is only defined and exported in
 * non-production builds. Next.js tree-shakes the dead branch in production.
 * The name prefix __ signals test-only; a lint rule or grep can enforce this.
 */
// MINOR-2 (correctness review): guard export to non-production builds so Next's
// dead-code elimination removes it from the prod bundle entirely.
// The `process.env.NODE_ENV !== 'production'` branch is statically evaluatable at
// build time — Webpack/SWC replaces it with `false` in prod, tree-shaking the body.
export const __resetForTesting: () => void =
  process.env.NODE_ENV !== 'production'
    ? function __resetForTestingImpl(): void {
        // ── Restore monkey-patched globals ──────────────────────────────────────
        // [1] window.history.pushState
        if (_installedOriginalPushState !== null && typeof window !== 'undefined') {
          window.history.pushState = _installedOriginalPushState;
          _installedOriginalPushState = null;
        }
        // [2] window.history.replaceState
        if (_installedOriginalReplaceState !== null && typeof window !== 'undefined') {
          window.history.replaceState = _installedOriginalReplaceState;
          _installedOriginalReplaceState = null;
        }
        // [3] window.fetch — fetchPatched=false is enough; installFetchPatch() checks it.
        // We do NOT restore window.fetch here: the original is captured with .bind() inside
        // installFetchPatch and closed over by the patched version, so restoring it would
        // require storing it at module level too. Resetting the flag is sufficient for tests
        // because each test uses a fresh jsdom env (Vitest isolates per file) and fetch is
        // not patched in typical unit tests (no RSC/server-action requests are made).
        fetchPatched = false;

        // ── Remove event listeners ──────────────────────────────────────────────
        if (typeof document !== 'undefined') {
          // [4] document 'click' (capture)
          if (_installedClickHandler !== null) {
            document.removeEventListener('click', _installedClickHandler as EventListener, true);
            _installedClickHandler = null;
          }
          // [6] document 'visibilitychange' (passive)
          if (_installedVisibilityHandler !== null) {
            document.removeEventListener(
              'visibilitychange',
              _installedVisibilityHandler as EventListener
            );
            _installedVisibilityHandler = null;
          }
        }
        if (typeof window !== 'undefined') {
          // [5] window 'popstate' (capture)
          if (_installedPopstateHandler !== null) {
            window.removeEventListener(
              'popstate',
              _installedPopstateHandler as EventListener,
              true
            );
            _installedPopstateHandler = null;
          }
          // [7] window 'pageshow' (passive)
          if (_installedPageshowHandler !== null) {
            window.removeEventListener('pageshow', _installedPageshowHandler as EventListener);
            _installedPageshowHandler = null;
          }
        }

        // ── Reset scalar flags and timers ([8]) ─────────────────────────────────
        installed = false;
        lastNavTs = -Infinity;
        lastClickTs = -1;
        lastClickTarget = undefined;
        lastClickIntendedUrl = null;
        lastTabVisibleTs = null;
        bfcacheRestored = false;
        lastRscBreadcrumbTs = -Infinity;
        if (stackedGhostTimer !== null) {
          clearTimeout(stackedGhostTimer);
          stackedGhostTimer = null;
        }
        currentUrl = '';

        // ── Reset collections ([9]) ─────────────────────────────────────────────
        queue.splice(0);
        fetchRing.splice(0);
        dedupMap.clear();
      }
    : /* istanbul ignore next */ function __resetForTestingNoop(): void {
        // Production no-op — dead-code-eliminated by Next's build (SWC/Webpack
        // replaces `process.env.NODE_ENV !== 'production'` with `false` in prod).
      };

const queue: NavEvent[] = [];
const dedupMap = new Map<string, { firstSeen: number; count: number }>();
// Upgrade 1: RSC/server-action correlation ring buffer.
const fetchRing: FetchRecord[] = [];

let queueSize = DEFAULT_QUEUE_SIZE;
let snapBackWindowMs = DEFAULT_SNAP_BACK_WINDOW_MS;
let wrongDestWindowMs = DEFAULT_WRONG_DEST_WINDOW_MS;
let minSinceClickMs = DEFAULT_MIN_SINCE_CLICK_MS;
let currentUrl = '';
let lastClickTs = -1;
let lastClickTarget: ClickTarget | undefined;
let lastClickIntendedUrl: string | null = null;

// Upgrade 2: tab-visibility tracking.
let lastTabVisibleTs: number | null = null;

// Upgrade 4: bfcache detection.
let bfcacheRestored = false;

// Upgrade 7 (Fix B): throttle the rsc-inflight breadcrumb to avoid flooding
// Sentry's ~100-entry breadcrumb buffer during a rapid-nav storm.
const RSC_BREADCRUMB_COOLDOWN_MS = 5_000;
let lastRscBreadcrumbTs = -Infinity;

// Stacked-ghost two-stage capture: arm at URL-settle, verify STACKED_GHOST_CHECK_DELAY_MS
// later whether [data-route-skeleton] count is >= 2 (structural DOM evidence of co-mount).
// 800ms chosen: long enough for React to finish painting the new route in fast cases,
// short enough to catch genuinely stuck states before users notice and navigate away.
const STACKED_GHOST_CHECK_DELAY_MS = 800;
let stackedGhostTimer: ReturnType<typeof setTimeout> | null = null;

// NOTE — hydration-aware gate REMOVED (adversarial review, 2026-05-28):
//
//   The former "hydration-aware gate" (lastHydrationErrorTs + window 'error' listener)
//   was designed to require a React #418/419 corroborating signal before firing on
//   same-URL stacked-ghost events. It was removed for two reasons:
//
//   (a) DEAD CODE: The data-layout-skeleton rename already fixes the false-positive root
//       cause. A normal /tasks load now shows route_skeleton_count === 1, which
//       early-returns at the count < 2 gate — the same-URL hydration branch was never
//       reached for the scenario it was written for.
//
//   (b) UNRELIABLE SIGNAL: React reports recoverable hydration errors via onRecoverableError(),
//       which does NOT reliably fire a global window 'error' event in production builds. The
//       gate would have been permanently silent in real conditions.
//
// WS-B VALIDATION STRATEGY: the React #418 fix (WS-B, separate branch) is validated by
// the NATIVE Sentry issue trend for the independent React hydration issue. That signal is
// reliable. This detector's sole job is genuine 2+ route-skeleton structural co-mounting.

// Window budget for "is a navigation currently in progress?" used by the third-party
// pushState breadcrumb (Fix A2). A new nav arms the stacked-ghost timer (800ms); we use
// a slightly wider window (2s) to catch concurrent third-party writes that happen after
// the stacked-ghost timer fires but within the same nav event cluster.
const NAV_IN_PROGRESS_WINDOW_MS = 2_000;
let lastNavTs = -Infinity;

// ── FIX-C: Singleton/listener tracking for __resetForTesting ─────────────────
// watcher.ts is a 6-round hotspot — every new listener or monkey-patch MUST be added
// to BOTH the install block AND the reset checklist below, or tests layer duplicate
// handlers across beforeEach calls.
//
// CHECKLIST — everything __resetForTesting must restore/remove:
//   Monkey-patches (restore to original):
//     [1] window.history.pushState
//     [2] window.history.replaceState
//     [3] window.fetch  (via installFetchPatch — tracked by fetchPatched flag)
//   Event listeners (removeEventListener):
//     [4] document 'click'        handleClick          capture=true
//     [5] window  'popstate'      popstateHandler      capture=true
//     [6] document 'visibilitychange' visibilityHandler  passive
//     [7] window  'pageshow'      pageshowHandler      passive
//   Scalars / timers:
//     [8]  installed, fetchPatched, lastNavTs, lastClickTs, lastClickTarget,
//          lastClickIntendedUrl, lastTabVisibleTs, bfcacheRestored,
//          lastRscBreadcrumbTs, currentUrl, stackedGhostTimer
//   Collections:
//     [9]  queue, fetchRing, dedupMap
//
// Handler refs — populated by installPhantomNavWatcher, cleared by __resetForTesting:
let _installedOriginalPushState: History['pushState'] | null = null;
let _installedOriginalReplaceState: History['replaceState'] | null = null;
// Named function refs so removeEventListener can find the exact registration.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _installedClickHandler: ((e: MouseEvent) => void) | null = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _installedPopstateHandler: ((e: PopStateEvent) => void) | null = null;
let _installedVisibilityHandler: (() => void) | null = null;
let _installedPageshowHandler: ((e: PageTransitionEvent) => void) | null = null;

export function installPhantomNavWatcher(options: InstallOptions = {}): void {
  const enabled = options.enabled ?? process.env.NODE_ENV === 'production';

  if (!enabled || installed || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  installed = true;
  queueSize = options.queueSize ?? DEFAULT_QUEUE_SIZE;
  snapBackWindowMs = options.snapBackWindowMs ?? DEFAULT_SNAP_BACK_WINDOW_MS;
  wrongDestWindowMs = options.wrongDestWindowMs ?? DEFAULT_WRONG_DEST_WINDOW_MS;
  minSinceClickMs = options.minSinceClickMs ?? DEFAULT_MIN_SINCE_CLICK_MS;
  currentUrl = window.location.href;

  // Upgrade 2: lastTabVisibleTs is initialized to null and only set inside the
  // visibilitychange handler when the tab transitions TO visible.
  // Do NOT seed it here at install time — a page that loads already-visible has
  // had no actual tab switch, so seeding would falsely report
  // tab_switch_within_snap_window=true for any snap within 8s of page load.

  // FIX-C: capture originals into module-level vars so __resetForTesting can restore them.
  const originalPushState = window.history.pushState.bind(window.history) as History['pushState'];
  const originalReplaceState = window.history.replaceState.bind(
    window.history
  ) as History['replaceState'];
  _installedOriginalPushState = originalPushState;
  _installedOriginalReplaceState = originalReplaceState;

  window.history.pushState = function patchedPushState(...args) {
    const fromUrl = window.location.href;
    // Upgrade 5: capture history.state BEFORE the push (this is "from" state).
    const fromState = window.history.state;
    // MINOR-1 (correctness review): read the new state from args[0] (the state argument
    // passed to THIS call) rather than from window.history.state after the write. This
    // avoids a same-frame race with other monkey-patches that may overwrite
    // window.history.state between our originalPushState call and our read.
    const newState = args[0];
    const result = originalPushState(...args);
    const toUrl = window.location.href;

    // SERIOUS-2 (correctness review): lastNavTs is set HERE — only for Next-initiated
    // writes (state with __NA key) — BEFORE captureThirdPartyHistoryWriteIfActive is
    // called. This ensures the first Next nav sets lastNavTs so a concurrent third-party
    // write immediately following it is captured. Third-party writes (no __NA) do NOT
    // update lastNavTs — otherwise the write would mark itself as "during an active nav".
    //
    // recordNavigation reads fromUrl and fromState from its parameters (captured above,
    // BEFORE the write), so the call-order does NOT affect the "from" data.
    const isNextWritePush =
      newState !== null &&
      typeof newState === 'object' &&
      '__NA' in (newState as Record<string, unknown>);
    if (isNextWritePush) {
      lastNavTs = performance.now();
    }

    // Upgrade 5: capture history.state AFTER the push (this is "to" state).
    recordNavigation('pushState', fromUrl, toUrl, fromState, window.history.state);

    // WS-A (Round 6 / A2): detect non-Next third-party pushState during an active nav.
    // The App Router always includes `__NA: true` in its history.state writes. A pushState
    // whose new state has NO `__NA` key during an active nav window is a strong candidate
    // for the third-party snap-back trigger documented in Phenomenon 2 (A2 finding).
    //
    // ADDITIVE ONLY — the breadcrumb is emitted AFTER the original call and does NOT
    // delay, suppress, or interfere with the actual history write or any router dispatch.
    // The note in the synthesis (Codex Q3) is clear: app-level fetch abort -> MPA hard-nav.
    // We observe only.
    //
    // PII audit: the new state's __NA key presence (boolean) and a stack trace stripped
    // to basenames only are sent. No user-controlled state content is included.
    captureThirdPartyHistoryWriteIfActive(newState, 'pushState');
    return result;
  };

  window.history.replaceState = function patchedReplaceState(...args) {
    const fromUrl = window.location.href;
    const fromState = window.history.state;
    // MINOR-1 (correctness review): read the new state from args[0] (the state argument
    // passed to THIS call), not from window.history.state after the write.
    const newState = args[0];
    const result = originalReplaceState(...args);
    const toUrl = window.location.href;

    // SERIOUS-2 (correctness review): same lastNavTs-only-for-Next-writes pattern as
    // patchedPushState — see comment there. Set lastNavTs here (before the third-party
    // check) ONLY when the new state carries __NA (Next-initiated write).
    const isNextWriteReplace =
      newState !== null &&
      typeof newState === 'object' &&
      '__NA' in (newState as Record<string, unknown>);
    if (isNextWriteReplace) {
      lastNavTs = performance.now();
    }

    recordNavigation('replaceState', fromUrl, toUrl, fromState, window.history.state);

    // WS-A (Round 6 / A2): same third-party detection for replaceState.
    captureThirdPartyHistoryWriteIfActive(newState, 'replaceState');
    return result;
  };

  // FIX-C: store named handler refs so __resetForTesting can removeEventListener them.
  // [4] document 'click' — capture phase so we see the target before any stopPropagation.
  _installedClickHandler = handleClick;
  document.addEventListener('click', _installedClickHandler, true);

  // [5] window 'popstate' — capture phase.
  _installedPopstateHandler = () => {
    const fromUrl = currentUrl || window.location.href;
    const toUrl = window.location.href;
    window.setTimeout(() => {
      // For popstate, fromState is unknowable synchronously (browser restores
      // the state on the event); we capture the current state after the delay.
      recordNavigation('popstate', fromUrl, toUrl, null, window.history.state);
    }, 0);
  };
  window.addEventListener('popstate', _installedPopstateHandler, true);

  // Upgrade 2: passive visibilitychange listener — no sync layout reads.
  // [6] document 'visibilitychange'.
  _installedVisibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      lastTabVisibleTs = performance.now();
    }
  };
  document.addEventListener('visibilitychange', _installedVisibilityHandler, { passive: true });

  // Upgrade 4: bfcache pageshow detection — passive, no layout reads.
  // [7] window 'pageshow'.
  _installedPageshowHandler = (event: PageTransitionEvent) => {
    if (event.persisted) {
      bfcacheRestored = true;
    }
  };
  window.addEventListener('pageshow', _installedPageshowHandler, { passive: true });

  // NOTE: the former window 'error' hydration-error listener was removed here.
  // See the comment block above the STACKED_GHOST_CHECK_DELAY_MS constant for rationale.

  // Upgrade 1: monkey-patch fetch idempotently.
  installFetchPatch();
}

function handleClick(event: MouseEvent): void {
  const now = performance.now();
  lastClickTs = now;
  lastClickTarget = describeClickTarget(event);
  lastClickIntendedUrl = getRawIntendedUrl(event);

  pushEvent({
    ts: now,
    kind: 'click',
    fromUrl: window.location.href,
    toUrl: window.location.href,
    sinceLastClickMs: 0,
    lastClickTarget,
    intendedUrl: lastClickIntendedUrl,
  });
}

function recordNavigation(
  kind: Exclude<NavKind, 'click'>,
  fromUrl: string,
  toUrl: string,
  // Upgrade 5: history.state before and after the navigation.
  historyStateFrom: unknown,
  historyStateTo: unknown
): void {
  currentUrl = toUrl;

  const now = performance.now();

  // WS-A (Round 6): lastNavTs is NOT set here — it is set explicitly by the caller
  // (patchedPushState / patchedReplaceState) ONLY for Next-initiated writes (state with
  // __NA key). Third-party writes (no __NA) go through recordNavigation too (URL DID
  // change) but must NOT update lastNavTs — that would make captureThirdPartyHistoryWriteIfActive
  // see "nav in progress" on the very write it is trying to classify as third-party.
  //
  // This split satisfies both SERIOUS-2 (first Next nav sets lastNavTs before the
  // concurrent third-party write's check) and the "no active nav" invariant (a
  // third-party write alone never marks itself as "during an active nav").

  // Upgrade 7 (Fix A): sample stacked-ghost signals here, at URL-settle time,
  // so capturePhantomNav reads values from the correct moment (before the rapid-nav
  // storm resolves) rather than from the later capture moment.
  // The RSC count is also reused by the Fix-B breadcrumb gate below — computed once.
  const rscInflightAtSettle = sampleRscInflightAtSettle();
  const staleSuspenseFallbackCount = sampleStaleSuspenseFallbackCount();
  const bodyElementCount = sampleBodyElementCount();

  const event: NavEvent = {
    ts: now,
    kind,
    fromUrl,
    toUrl,
    sinceLastClickMs: getSinceLastClickMs(),
    lastClickTarget,
    intendedUrl: lastClickIntendedUrl,
    historyStateFrom,
    historyStateTo,
    rscInflightAtSettle,
    staleSuspenseFallbackCount,
    bodyElementCount,
  };

  pushEvent(event);

  // Upgrade 7 (Fix B): stacked-ghost detection breadcrumb at URL settle.
  // Throttled to RSC_BREADCRUMB_COOLDOWN_MS to avoid flooding Sentry's ~100-entry
  // breadcrumb buffer during a rapid-nav storm (re: patterns-reviewer rule, Upgrade 6).
  // Reuses rscInflightAtSettle computed above — no duplicate fetchRing scan.
  // PII audit: numeric count and sanitized URL only — no raw content.
  try {
    if (
      rscInflightAtSettle !== null &&
      rscInflightAtSettle >= 2 &&
      now - lastRscBreadcrumbTs > RSC_BREADCRUMB_COOLDOWN_MS
    ) {
      lastRscBreadcrumbTs = now;
      Sentry.addBreadcrumb({
        category: 'phantom-nav.rsc-inflight',
        message: `${rscInflightAtSettle} RSC fetches in-flight at URL settle`,
        level: 'info',
        data: {
          rsc_inflight_count: rscInflightAtSettle,
          settled_url: sanitizeUrlForTelemetry(toUrl),
        },
      });
    }
  } catch (_e) {
    // intentional: telemetry breadcrumb must never throw
  }

  // Stacked-ghost Stage 1 — arm the deferred verify on EVERY navigation.
  // Cancel-and-rearm: the page the user actually lands on is the only one worth
  // checking, so a new navigation always supersedes the pending check.
  // No RSC-inflight threshold here — Stage 2 (verifyStackedGhost) is the gate.
  // SSR guard: typeof window check ensures no timer during server-side rendering.
  if (typeof window !== 'undefined') {
    if (stackedGhostTimer !== null) {
      clearTimeout(stackedGhostTimer);
      stackedGhostTimer = null;
    }
    stackedGhostTimer = setTimeout(
      () => verifyStackedGhost(toUrl, now),
      STACKED_GHOST_CHECK_DELAY_MS
    );
  }

  detectPhantomNav(event);
}

function pushEvent(event: NavEvent): void {
  queue.push(event);
  if (queue.length > queueSize) {
    queue.splice(0, queue.length - queueSize);
  }
}

function detectPhantomNav(e2: NavEvent): void {
  const e2Index = queue.length - 1;
  const e1 = findPriorEvent(e2Index);
  const e0 = findPriorEvent(e2Index, 2);

  const intendedUrl = getEventIntendedUrl(e2);

  if (
    intendedUrl &&
    e2.sinceLastClickMs >= 0 &&
    e2.sinceLastClickMs < minSinceClickMs &&
    e2.sinceLastClickMs < wrongDestWindowMs &&
    !urlsMatch(e2.toUrl, intendedUrl) &&
    !urlsMatch(e2.toUrl, e2.fromUrl) &&
    // Skip documented index→default config redirects (e.g. /settings → /settings/profile).
    // These are a by-design 307/308 in next.config.ts, not a phantom-nav — firing on them
    // produced the EJ/E8 false-positives (Round 5, 2026-05-26).
    !isAllowlistedConfigRedirect(intendedUrl, e2.toUrl)
  ) {
    capturePhantomNav(
      'wrong-destination',
      e1?.kind === 'click' ? e1 : undefined,
      e2,
      e2,
      intendedUrl
    );
  }

  if (
    e1 &&
    // FP guard (Sentry 120443030, 42 ev / 7 users): the snap event must actually move
    // the user to a DIFFERENT page. Kanban filter/search URL syncs (?f___search__=) and
    // HistoryUpdater same-URL replaceState churn keep every URL in the window on the
    // same pathname, so the from-URL match below is satisfied trivially — a "snap-back"
    // landing on the pathname it started from is filter-state churn the user cannot
    // perceive as navigation. Query-level reverts are exclusively the tab-snap rule's
    // job. The real bug this rule hunts (router.back() teleports, late-RSC canonicalUrl
    // commits) always changes the pathname.
    getPathname(e2.fromUrl) !== getPathname(e2.toUrl) &&
    urlsMatch(e2.toUrl, e0?.fromUrl ?? e1.fromUrl) &&
    e2.ts - e1.ts < snapBackWindowMs &&
    e2.sinceLastClickMs > minSinceClickMs &&
    ((e0?.sinceLastClickMs ?? Number.POSITIVE_INFINITY) < minSinceClickMs ||
      e1.sinceLastClickMs < minSinceClickMs)
  ) {
    capturePhantomNav('snap-back', e0 ?? e1, e1, e2, getEventIntendedUrl(e1));
  }

  if (
    e1 &&
    getPathname(e1.toUrl) === getPathname(e2.toUrl) &&
    e2.sinceLastClickMs > minSinceClickMs &&
    e2.ts - e1.ts < TAB_SNAP_WINDOW_MS &&
    isTabSnapRevert(e1.fromUrl, e1.toUrl, e2.toUrl)
  ) {
    capturePhantomNav('tab-snap', e0, e1, e2, getEventIntendedUrl(e1));
  }
}

function capturePhantomNav(
  kind: PhantomNavPattern,
  e0: NavEvent | undefined,
  e1: NavEvent,
  e2: NavEvent,
  intendedUrl: string | null
): void {
  // Sanitize the pathnames before they touch Sentry. A raw pathname can carry a UUID/
  // numeric id (e.g. /leads/<uuid>) = PII, and these values flow into the breadcrumb
  // message + data, the tags, the fingerprint, AND the dedup key below. The detection
  // RULES above (detectPhantomNav) keep using raw getPathname() for matching; only this
  // telemetry-facing copy is redacted. sanitizeUrlForTelemetry redacts UUID/numeric-id
  // segments while leaving static routes (/ai, /tasks, /settings) untouched, so Sentry
  // issue grouping stays stable for the common case.
  const fromPathname = sanitizeUrlForTelemetry(getPathname(e2.fromUrl));
  const toPathname = sanitizeUrlForTelemetry(getPathname(e2.toUrl));

  // Upgrade 1: collect in-flight / recently-settled RSC+server-action requests.
  const inflightRequests = collectInflightRequests(e2.ts);

  // Upgrade 2: compute tab-visibility signals.
  const msSinceTabVisible = lastTabVisibleTs !== null ? Math.round(e2.ts - lastTabVisibleTs) : null;
  const tabSwitchWithinSnapWindow =
    msSinceTabVisible !== null && msSinceTabVisible >= 0 && msSinceTabVisible < snapBackWindowMs;

  // Upgrade 6: compute confidence score.
  const confidence = computeConfidence(inflightRequests, tabSwitchWithinSnapWindow, e0, e1, e2);

  if (!shouldCapture(kind, fromPathname, toPathname, confidence)) {
    return;
  }

  Sentry.addBreadcrumb({
    category: 'phantom-nav.captured',
    message: `${kind}: ${fromPathname} -> ${toPathname}`,
    level: kind === 'tab-snap' ? 'info' : 'warning',
    data: {
      from_pathname: fromPathname,
      to_pathname: toPathname,
      time_since_click_ms: e2.sinceLastClickMs,
      time_between_navs_ms: Math.round(e2.ts - e1.ts),
      confidence,
    },
  });

  // Upgrade 3: capture stack at the phantom pushState (only for non-tab-snap).
  const triggerStack = kind !== 'tab-snap' ? captureStack() : null;

  // Upgrade 4: read nav entry type once at capture time.
  const navEntryType = getNavEntryType();

  // Upgrade 5: build history.state descriptors from the snap event (e2).
  // e2 is the phantom snap navigation — its historyStateFrom is the state before the
  // snap and historyStateTo is the state after. Using e1.historyStateFrom was a bug:
  // e1 is the intended navigation, not the snap event.
  const historyStateFrom = describeHistoryState(e2.historyStateFrom);
  const historyStateTo = describeHistoryState(e2.historyStateTo);

  // Corruption is a CLOBBER, computed by comparing the from/to descriptors — never from a
  // single state in isolation (which would re-flag benign third-party pushState writes).
  // Only emit the corruption signal when Next's __NA anchor was present before the snap and
  // lost after it. Stamp it onto the `to` descriptor so existing Sentry context/queries
  // reading history_state_to.is_corrupt see the real signal.
  const isCorruptClobber = isHistoryStateClobber(historyStateFrom, historyStateTo);
  if (historyStateTo) {
    historyStateTo.is_corrupt = isCorruptClobber;
  }

  // Upgrade 7 (Fix A): read stacked-ghost signals from the snap event (e2).
  // These were sampled at URL-settle time in recordNavigation — using them here
  // avoids false zeros that occur when the rapid-nav storm resolves before capture.
  // ?? null: fields are optional on click events (which have no settle moment).
  const rscInflightAtSettle = e2.rscInflightAtSettle ?? null;
  const staleSuspenseFallbackCount = e2.staleSuspenseFallbackCount ?? null;
  const bodyElementCount = e2.bodyElementCount ?? null;

  const phantomNav: PhantomNavEvent = {
    from_url: e0?.fromUrl ? sanitizeUrlForTelemetry(e0.fromUrl) : null,
    intended_url: intendedUrl ? sanitizeUrlForTelemetry(intendedUrl) : null,
    actual_url: sanitizeUrlForTelemetry(e1.toUrl),
    snap_url: sanitizeUrlForTelemetry(e2.toUrl),
    time_since_click_ms: e2.sinceLastClickMs,
    time_between_navs_ms: e2.ts - e1.ts,
    last_click_target: e1.lastClickTarget ?? null,
    queue: queue.slice(-10).map(eventToBreadcrumb),
    build_sha: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? null,
    // Upgrade 1:
    inflight_request_count: inflightRequests.length,
    inflight_requests: inflightRequests.slice(0, 10),
    // Upgrade 2:
    ms_since_tab_visible: msSinceTabVisible,
    tab_switch_within_snap_window: tabSwitchWithinSnapWindow,
    // Upgrade 3:
    trigger_stack: triggerStack,
    // Upgrade 4:
    nav_entry_type: navEntryType,
    bfcache_restore: bfcacheRestored,
    // Upgrade 5:
    history_state_from: historyStateFrom,
    history_state_to: historyStateTo,
    // Upgrade 6:
    confidence,
    // Upgrade 7:
    rsc_inflight_at_settle: rscInflightAtSettle,
    stale_suspense_fallback_count: staleSuspenseFallbackCount,
    body_element_count: bodyElementCount,
    route_skeleton_count: null, // only populated in the stacked-ghost path
  };

  Sentry.captureMessage(`phantom-nav: ${kind}`, {
    level: kind === 'tab-snap' ? 'info' : 'warning',
    tags: {
      'phantom-nav': 'true',
      'phantom-nav-kind': kind,
      'phantom-nav-from-pathname': fromPathname,
      'phantom-nav-to-pathname': toPathname,
      // Upgrade 6: confidence tag enables Sentry issue grouping by quality.
      'phantom-nav-confidence': confidence,
    },
    contexts: {
      phantom_nav: phantomNav as unknown as Record<string, unknown>,
    },
    fingerprint: ['phantom-nav', kind, fromPathname, toPathname],
  });
}

// Upgrade 6: dedup key now includes confidence so high-confidence events are
// never starved by low-confidence noise on the same route pair.
function shouldCapture(
  kind: PhantomNavPattern,
  fromPathname: string,
  toPathname: string,
  confidence: PhantomNavConfidence
): boolean {
  const now = Date.now();
  const key = `${kind}:${fromPathname}:${toPathname}:${confidence}`;
  const entry = dedupMap.get(key);

  if (!entry || now - entry.firstSeen >= DEDUP_WINDOW_MS) {
    dedupMap.set(key, { firstSeen: now, count: 1 });
    evictOldDedupEntries(now);
    return true;
  }

  if (entry.count >= DEDUP_LIMIT) {
    return false;
  }

  entry.count++;
  return true;
}

function evictOldDedupEntries(now: number): void {
  if (dedupMap.size <= 50) {
    return;
  }

  dedupMap.forEach((entry, key) => {
    if (now - entry.firstSeen > DEDUP_WINDOW_MS * 2) {
      dedupMap.delete(key);
    }
  });
}

function findPriorEvent(fromIndex: number, offset = 1): NavEvent | undefined {
  let seen = 0;

  for (let index = fromIndex - 1; index >= 0; index--) {
    if (queue[index].kind === 'click') {
      continue;
    }

    seen++;
    if (seen === offset) {
      return queue[index];
    }
  }

  return undefined;
}

function describeClickTarget(event: MouseEvent): ClickTarget {
  const rawTarget = event.target instanceof Element ? event.target : null;
  const target =
    rawTarget?.closest('a, button, [role="button"]') ?? rawTarget ?? document.documentElement;
  const anchor = target.closest('a');
  const button = target.closest('button, [role="button"]');
  const dataHref =
    target.getAttribute('data-href') ?? target.getAttribute('data-onboarding-target') ?? null;
  // Record only whether aria-label is present — never its value.
  // CRM aria-labels are interpolated with entity data (lead name, task title, phone, email)
  // which would leak PII into Sentry telemetry.
  const hasAriaLabel = !!(button?.getAttribute('aria-label') ?? target.getAttribute('aria-label'));

  return {
    tag: target.tagName.toLowerCase(),
    text: '',
    href: anchor instanceof HTMLAnchorElement ? sanitizeUrlForTelemetry(anchor.href) : null,
    dataHref: dataHref ? sanitizeUrlForTelemetry(dataHref) : null,
    hasAriaLabel,
  };
}

function getRawIntendedUrl(event: MouseEvent): string | null {
  const rawTarget = event.target instanceof Element ? event.target : null;
  const target =
    rawTarget?.closest('a, button, [role="button"]') ?? rawTarget ?? document.documentElement;
  const anchor = target.closest('a');
  const dataHref =
    target.getAttribute('data-href') ?? target.getAttribute('data-onboarding-target') ?? null;
  const raw = anchor instanceof HTMLAnchorElement ? anchor.href : dataHref;

  if (!raw || !isUrlLike(raw)) {
    return null;
  }

  return normalizeUrl(raw);
}

function getSinceLastClickMs(): number {
  if (lastClickTs < 0) {
    return -1;
  }

  return performance.now() - lastClickTs;
}

function getEventIntendedUrl(event: NavEvent | undefined): string | null {
  return event?.intendedUrl ?? null;
}

function isUrlLike(value: string): boolean {
  return (
    /^(https?:)?\/\//i.test(value) ||
    value.startsWith('/') ||
    value.startsWith('?') ||
    value.startsWith('#')
  );
}

function urlsMatch(left: string | null | undefined, right: string | null | undefined): boolean {
  const normalizedLeft = left ? normalizeUrl(left) : null;
  const normalizedRight = right ? normalizeUrl(right) : null;

  if (!normalizedLeft || !normalizedRight) {
    return false;
  }

  if (normalizedLeft === normalizedRight) {
    return true;
  }

  const leftParts = getUrlParts(normalizedLeft);
  const rightParts = getUrlParts(normalizedRight);

  return (
    leftParts.origin === rightParts.origin &&
    leftParts.pathname === rightParts.pathname &&
    leftParts.search === rightParts.search &&
    leftParts.hash === rightParts.hash
  );
}

function normalizeUrl(url: string): string | null {
  try {
    return new URL(url, window.location.href).href;
  } catch (_error) {
    // intentional: malformed telemetry URLs should not create reporting noise
    return null;
  }
}

/**
 * Sanitize a URL for Sentry telemetry. Keep pathname structure for debugging, but redact
 * identifiers and query strings that may carry lead names, search terms, or other PII.
 */
function sanitizeUrlForTelemetry(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.href);
  } catch (_error) {
    // intentional: malformed telemetry URLs are represented as a safe sentinel
    return '[invalid-url]';
  }

  const safePath = parsed.pathname
    .split('/')
    .map((segment) => {
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
        return '[uuid]';
      }

      if (/^\d{4,}$/.test(segment)) {
        return '[id]';
      }

      return segment;
    })
    .join('/');

  const safeSearchParams = new URLSearchParams();
  parsed.searchParams.forEach((value, key) => {
    if (SAFE_QUERY_KEYS.has(key)) {
      safeSearchParams.append(key, value);
    }
  });
  const safeSearch = safeSearchParams.toString();

  return safeSearch ? `${safePath}?${safeSearch}` : safePath;
}

function getUrlParts(url: string): UrlParts {
  try {
    const parsed = new URL(url, window.location.href);

    return {
      origin: parsed.origin,
      pathname: parsed.pathname,
      search: parsed.search,
      hash: parsed.hash,
    };
  } catch (_error) {
    // intentional: malformed telemetry URLs fall back to the current page URL
    return {
      origin: window.location.origin,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    };
  }
}

function getPathname(url: string): string {
  return getUrlParts(url).pathname || '/';
}

/**
 * True when intendedUrl → toUrl is a documented index→default config redirect
 * (next.config.ts `redirects()`), e.g. `/settings` → `/settings/profile`.
 *
 * The wrong-destination rule compares the landed URL to the clicked link's href. A
 * by-design server redirect (307/308) makes them legitimately differ — that is NOT a
 * phantom-nav. This helper lets the rule skip those known redirects while still firing on
 * a genuine, undocumented destination mismatch (the actual router defect we hunt).
 *
 * Compares PATHNAMES only (search/hash ignored) so `/settings?x=1` → `/settings/profile`
 * still matches. Exported for unit testing (Rule #48 regression coverage).
 */
export function isAllowlistedConfigRedirect(
  intendedUrl: string | null | undefined,
  toUrl: string | null | undefined
): boolean {
  if (!intendedUrl || !toUrl) {
    return false;
  }
  try {
    const intendedPath = getPathname(intendedUrl);
    const expectedDest = KNOWN_CONFIG_REDIRECTS[intendedPath];
    if (!expectedDest) {
      return false;
    }
    // Pathname-level compare: allowlist values keep the config's exact destination
    // (which may carry query params, e.g. /ig), but a redirect landing is judged by
    // path — attribution params never make a by-design redirect look phantom.
    return getPathname(toUrl) === getPathname(expectedDest);
  } catch (_e) {
    // intentional: malformed URL — treat as not-allowlisted (let the rule decide)
    return false;
  }
}

function eventToBreadcrumb(event: NavEvent): PhantomNavEvent['queue'][number] {
  return {
    ts: Math.round(event.ts),
    kind: event.kind,
    from_url: sanitizeUrlForTelemetry(event.fromUrl),
    to_url: sanitizeUrlForTelemetry(event.toUrl),
    since_last_click_ms: Math.round(event.sinceLastClickMs),
    last_click_target: event.lastClickTarget ?? null,
  };
}

/**
 * True when e1→e2 shows a REVERTED tab-style param: exactly one TAB_SNAP_PARAM_KEYS key
 * differs between beforeUrl (e1.toUrl) and afterUrl (e2.toUrl), AND the after value
 * equals that key's value in priorUrl (e1.fromUrl) — i.e. the A → B → A pattern of the
 * router undoing the user's tab switch with no user input.
 *
 * FP guard (Sentry 131421061): the previous shape-only check ("exactly one tab-ish key
 * changed between e1.toUrl and e2.toUrl") fired on the FORWARD transition of the user's
 * own tab click whenever the router commit landed >300ms after the click with another
 * nav within 1.5s — i.e. on every slow tab switch on a busy page (/leads Archived tab,
 * live PROD event 2026-07-01). Requiring the revert makes a fresh forward change
 * (null → 'archived') structurally incapable of matching: after === prior only when the
 * value went somewhere and came back.
 */
function isTabSnapRevert(priorUrl: string, beforeUrl: string, afterUrl: string): boolean {
  const priorParams = new URL(priorUrl, window.location.href).searchParams;
  const beforeParams = new URL(beforeUrl, window.location.href).searchParams;
  const afterParams = new URL(afterUrl, window.location.href).searchParams;
  const changedKeys = new Set<string>();

  beforeParams.forEach((value, key) => {
    if (afterParams.get(key) !== value) {
      changedKeys.add(key);
    }
  });
  afterParams.forEach((value, key) => {
    if (beforeParams.get(key) !== value) {
      changedKeys.add(key);
    }
  });

  if (changedKeys.size !== 1) {
    return false;
  }

  const [changedKey] = Array.from(changedKeys);
  if (!TAB_SNAP_PARAM_KEYS.has(changedKey)) {
    return false;
  }

  // The revert requirement: the snapped-to value must equal the value the param held
  // BEFORE the intended change (null-safe: both absent counts as equal — e.g. the
  // param was added by e1 and stripped again by e2).
  return afterParams.get(changedKey) === priorParams.get(changedKey);
}

function truncate(value: string, maxLength: number): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}

// ── Upgrade 1: RSC/server-action fetch ring buffer ───────────────────────────

/**
 * Idempotently monkey-patches window.fetch to track RSC navigations and server-action
 * requests into fetchRing. Safe to call multiple times (fetchPatched guard).
 *
 * PII audit: only the sanitized URL, HTTP method, classification header, status code,
 * and Content-Length (a byte count) are recorded. No request or response bodies.
 * No cookie, auth token, or form-field data.
 *
 * Performance: one passive wrapper per fetch call; no sync layout reads; ring buffer
 * capped at FETCH_RING_MAX to bound memory; eviction on every record.
 */
function installFetchPatch(): void {
  if (fetchPatched || typeof window === 'undefined') {
    return;
  }

  fetchPatched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    // Determine URL and method without reading bodies.
    let rawUrl: string;
    let method = 'GET';

    if (typeof input === 'string') {
      rawUrl = input;
    } else if (input instanceof URL) {
      rawUrl = input.href;
    } else {
      // Request object
      rawUrl = input.url;
      method = input.method ?? 'GET';
    }

    if (init?.method) {
      method = init.method.toUpperCase();
    } else if (!(typeof input === 'string' || input instanceof URL)) {
      method = (input as Request).method?.toUpperCase() ?? 'GET';
    }

    // Classify the request. Only track RSC navigations and server actions.
    // RSC: URL contains ?_rsc= OR request carries RSC / Next-Router-* headers.
    // Server action: POST to same-origin page route with Next-Action header.
    let headerKind: 'rsc' | 'server-action' | null = null;
    let isPrefetch = false;

    // Resolve the header bag once — used for both classification and prefetch detection.
    // When input is a Request object and no init is provided, headers live on the
    // Request itself — fall back to input.headers for that case.
    const headerBag: HeadersInit | undefined =
      init?.headers ??
      (!(typeof input === 'string' || input instanceof URL)
        ? (input as Request).headers
        : undefined);

    // Case-insensitive header accessor that handles all three HeadersInit shapes.
    const getHeader = (name: string): string | null => {
      if (!headerBag) return null;
      if (headerBag instanceof Headers) {
        return headerBag.get(name);
      }
      if (Array.isArray(headerBag)) {
        const pair = headerBag.find(([k]) => k.toLowerCase() === name.toLowerCase());
        return pair ? pair[1] : null;
      }
      if (typeof headerBag === 'object') {
        const obj = headerBag as Record<string, string>;
        const lowerName = name.toLowerCase();
        for (const k of Object.keys(obj)) {
          if (k.toLowerCase() === lowerName) return obj[k];
        }
      }
      return null;
    };

    try {
      const url = new URL(rawUrl, window.location.href);
      const isSameOrigin = url.origin === window.location.origin;

      if (isSameOrigin) {
        if (url.searchParams.has('_rsc')) {
          headerKind = 'rsc';
          // ?_rsc= requests may also carry Next-Router-Prefetch when triggered by
          // <Link prefetch> — detect here so the flag is set regardless of which
          // classification branch ran.
          isPrefetch = getHeader('Next-Router-Prefetch') === '1';
        } else if (headerBag) {
          if (getHeader('RSC') === '1' || getHeader('Next-Router-State-Tree') !== null) {
            headerKind = 'rsc';
            isPrefetch = getHeader('Next-Router-Prefetch') === '1';
          } else if (method === 'POST' && getHeader('Next-Action') !== null) {
            headerKind = 'server-action';
          }
        }
      }
    } catch (_e) {
      // intentional: malformed URL — skip classification, don't track
    }

    if (headerKind === null) {
      // Not an RSC or server-action request — pass through untracked.
      return originalFetch(input, init);
    }

    const sanitizedUrl = (() => {
      try {
        return sanitizeUrlForTelemetry(rawUrl);
      } catch (_e) {
        return '[sanitize-error]';
      }
    })();

    const record: FetchRecord = {
      url: sanitizedUrl,
      method,
      headerKind,
      isPrefetch,
      startTs: performance.now(),
    };

    pushFetchRecord(record);

    return originalFetch(input, init).then(
      (response) => {
        record.settleTs = performance.now();
        record.status = response.status;
        const cl = response.headers.get('content-length');
        if (cl !== null) {
          const parsed = parseInt(cl, 10);
          if (!isNaN(parsed)) {
            record.responseSizeBytes = parsed;
          }
        }
        return response;
      },
      (err: unknown) => {
        record.settleTs = performance.now();
        record.status = 0; // network error / abort
        throw err;
      }
    );
  };
}

function pushFetchRecord(record: FetchRecord): void {
  fetchRing.push(record);
  if (fetchRing.length > FETCH_RING_MAX) {
    fetchRing.splice(0, fetchRing.length - FETCH_RING_MAX);
  }
}

/**
 * At snap time, collect all RSC/server-action requests that were either:
 * - Still in flight (no settleTs), OR
 * - Settled within FETCH_RECENT_SETTLE_WINDOW_MS before the snap.
 *
 * snapTs is performance.now() at detection time (e2.ts).
 */
function collectInflightRequests(snapTs: number): InFlightRequest[] {
  const result: InFlightRequest[] = [];

  for (const rec of fetchRing) {
    const inFlight = rec.settleTs === undefined;
    const recentlySettled =
      !inFlight &&
      rec.settleTs !== undefined &&
      snapTs - rec.settleTs < FETCH_RECENT_SETTLE_WINDOW_MS;

    if (!inFlight && !recentlySettled) {
      continue;
    }

    result.push({
      url: rec.url,
      method: rec.method,
      header_kind: rec.headerKind,
      in_flight: inFlight,
      age_ms: Math.round(snapTs - rec.startTs),
      status: rec.status ?? null,
      response_size_bytes: rec.responseSizeBytes ?? null,
    });
  }

  return result;
}

// ── Upgrade 3: stack capture ─────────────────────────────────────────────────

/**
 * Capture top ~15 cleaned stack frames. Strips absolute paths (file:///... or
 * webpack-internal:///...) to basenames only, removes query strings and hash
 * fragments from frame URLs so no app route info leaks through the stack.
 * Wrapped in try/catch — must never throw from telemetry.
 *
 * PII audit: only file basenames and line:col numbers are retained. No URL
 * pathnames, no query strings, no user-controlled content from Error.message.
 */
function captureStack(): string[] | null {
  try {
    const raw = new Error().stack;
    if (!raw) return null;

    return raw
      .split('\n')
      .slice(1) // drop the "Error" header line
      .slice(0, 15) // top 15 frames
      .map((frame) => {
        // Strip leading whitespace and "at ".
        let f = frame.trim().replace(/^at\s+/, '');
        // Replace any URL-like token (contains ://, starts with /, or is a
        // webpack-internal path) with just the basename + line:col.
        f = f.replace(
          /(?:https?:\/\/[^\s)]+|webpack-internal:\/\/[^\s)]+|\/[^\s):]+)/g,
          (match) => {
            // Extract basename and optional :line:col suffix.
            const withoutQuery = match.split('?')[0].split('#')[0];
            const lastSlash = withoutQuery.lastIndexOf('/');
            return lastSlash >= 0 ? withoutQuery.slice(lastSlash + 1) : withoutQuery;
          }
        );
        return f;
      })
      .filter((f) => f.length > 0);
  } catch (_e) {
    // intentional: stack capture is best-effort; never throw from telemetry
    return null;
  }
}

// ── Upgrade 4: navigation entry type ─────────────────────────────────────────

function getNavEntryType(): string | null {
  try {
    const entries = performance.getEntriesByType('navigation');
    if (entries.length > 0) {
      return (entries[0] as PerformanceNavigationTiming).type ?? null;
    }
  } catch (_e) {
    // intentional: Performance API may be unavailable in some environments
  }
  return null;
}

// ── Upgrade 5: history.state descriptor ──────────────────────────────────────

/**
 * Build a shallow, PII-safe descriptor of a history.state value.
 *
 * Next.js 16 App Router writes history.state as
 *   `{ __NA: true, __PRIVATE_NEXTJS_INTERNALS_TREE: {...} }`
 * — there is NO `idx` and NO `url` key (those were Pages Router). The previous
 * implementation checked `'url' in s` and `typeof s['idx'] === 'number'`, so it always
 * reported `has_url_key:false, idx:null` for EVERY healthy navigation — a false
 * "corruption" signal that made the live PROD captures look malformed when they were fine.
 *
 * This descriptor is PURELY OBSERVATIONAL: it records whether the App-Router anchor keys
 * are present (`has_na_key`, `has_internals_tree`), whether the state was empty/null
 * (`is_empty`), and a stray numeric `idx` if any. It does NOT decide corruption in
 * isolation — `is_corrupt` is ALWAYS `false` here.
 *
 * A single state object missing `__NA` is NOT corruption on its own: third-party code
 * (analytics, browser extensions, chat widgets) routinely calls `pushState`/`replaceState`
 * with its own state or `{}`, and Next itself uses empty/null states transiently. Hard-
 * flagging those re-polluted the exact signal WS2 just cleaned (same expected-error family
 * as the 23505-race / task-mutation / permission-denied Sentry-noise fixes — see
 * knowledge/bug-patterns.md). The genuine corruption signal is a CLOBBER — the prior state
 * HAD `__NA` and the new state LOST it — computed at the call site (capturePhantomNav) by
 * comparing the from/to descriptors via `isHistoryStateClobber()`.
 *
 * Exported for unit testing (Rule #48 regression coverage); only called internally by
 * capturePhantomNav otherwise.
 *
 * PII audit: boolean key-presence + a single numeric idx value — no PII.
 */
export function describeHistoryState(state: unknown): HistoryStateDescriptor | null {
  try {
    if (state === null || state === undefined || typeof state !== 'object') {
      return {
        has_na_key: false,
        has_internals_tree: false,
        is_corrupt: false, // empty/null is not corruption (e.g. popstate from-state)
        idx: null,
        is_empty: true,
      };
    }
    const s = state as Record<string, unknown>;
    const idx = typeof s['idx'] === 'number' ? s['idx'] : null;
    const hasNaKey = '__NA' in s;
    // Empty object {} is NOT corruption — third-party pushState/replaceState commonly writes
    // an empty state; flagging it as corrupt re-polluted the signal WS2 cleaned.
    const isEmpty = Object.keys(s).length === 0;
    return {
      has_na_key: hasNaKey,
      has_internals_tree: '__PRIVATE_NEXTJS_INTERNALS_TREE' in s,
      // is_corrupt is NEVER set from a single state in isolation — a non-empty object
      // missing __NA is most often a benign third-party state write. The real corruption
      // signal is a CLOBBER (from had __NA, to lost it), computed at the call site below.
      is_corrupt: false,
      idx,
      is_empty: isEmpty,
    };
  } catch (_e) {
    // intentional: state inspection is best-effort
    return null;
  }
}

/**
 * The genuine App-Router history.state corruption signal: a CLOBBER.
 *
 * Returns true only when the prior (from) state carried Next's `__NA` anchor and the new
 * (to) state LOST it — i.e. something overwrote Next's router state with a non-Next state.
 * A benign third-party state write where `__NA` was never involved on either side
 * (from.has_na_key === false) is NOT a clobber and must NOT be flagged. Likewise, a write
 * that preserves `__NA` (to.has_na_key === true) is healthy.
 *
 * Both inputs are PII-safe boolean descriptors; this function reads only `has_na_key`.
 * Exported for unit testing (Rule #48 regression coverage).
 */
export function isHistoryStateClobber(
  from: HistoryStateDescriptor | null,
  to: HistoryStateDescriptor | null
): boolean {
  if (!from || !to) {
    return false;
  }
  return from.has_na_key === true && to.has_na_key === false;
}

// ── WS-A (Round 6): A2 — third-party history write breadcrumb ────────────────

/**
 * Emit a Sentry breadcrumb when a pushState/replaceState write has NO `__NA` key
 * (i.e. it is NOT a Next.js App Router write) AND a navigation is currently in
 * progress (last nav within NAV_IN_PROGRESS_WINDOW_MS).
 *
 * PURPOSE: help find the snap-back's third-party trigger. The A2 analysis (Phenomenon 2)
 * identified that a non-Next pushState during the nav window dispatches ACTION_RESTORE with
 * window.location.href (possibly still the stale URL) and snaps canonicalUrl. If such a
 * write exists, the breadcrumb will appear in the Sentry replay/event before the snap.
 *
 * ADDITIVE ONLY: called AFTER the original history call; does not delay, suppress, or
 * interfere with the write or any router dispatch. No state, no DOM access.
 *
 * PII audit: only the kind (pushState/replaceState), the sanitized current URL, and a
 * basename-only stack trace are included. No state object content.
 *
 * @param newState  The state argument passed to this pushState/replaceState call (args[0]).
 *                  MINOR-1: this is read from the call argument, NOT from window.history.state
 *                  after the write, to avoid a same-frame race with other monkey-patches.
 * @param kind      'pushState' | 'replaceState' — for breadcrumb labelling.
 */
function captureThirdPartyHistoryWriteIfActive(
  newState: unknown,
  kind: 'pushState' | 'replaceState'
): void {
  try {
    // Only fire when a navigation is in progress (within the last NAV_IN_PROGRESS_WINDOW_MS).
    const now = performance.now();
    if (now - lastNavTs > NAV_IN_PROGRESS_WINDOW_MS) {
      return; // No active nav — benign third-party write, ignore.
    }

    // A Next.js App Router write ALWAYS includes the __NA key.
    // Any state without __NA is a third-party (or empty-state) write.
    const isNextWrite =
      newState !== null &&
      typeof newState === 'object' &&
      '__NA' in (newState as Record<string, unknown>);

    if (isNextWrite) {
      return; // Next-internal write — not the pattern we are looking for.
    }

    Sentry.addBreadcrumb({
      category: 'phantom-nav.third-party-history',
      message: `non-Next ${kind} during active nav (no __NA key)`,
      level: 'warning',
      data: {
        kind,
        // sanitized URL — no PII path segments beyond static route names.
        url: sanitizeUrlForTelemetry(window.location.href),
        // Basename-only stack trace — identifies the calling library without leaking paths.
        stack: captureStack(),
        ms_since_last_nav: Math.round(now - lastNavTs),
      },
    });
  } catch (_e) {
    // intentional: breadcrumb capture must never throw
  }
}

// ── Upgrade 7: stacked-ghost sampling helpers ─────────────────────────────────

/**
 * Count RSC navigation fetches still in-flight at the current moment.
 * Excludes prefetch requests (isPrefetch === true) — Next.js routinely has 2-5
 * background prefetches in flight on a healthy dashboard. Counting those was the
 * root cause of the false-positive stacked-ghost fires in production.
 * Fix A: called inside recordNavigation at URL-settle time (not at capture time),
 * so the value reflects the stacked-ghost window before the storm resolves.
 * Result is stored on NavEvent.rscInflightAtSettle and read back in capturePhantomNav.
 * Uses the existing fetchRing — no new state. Wrapped in try/catch.
 * PII audit: numeric count only.
 */
function sampleRscInflightAtSettle(): number | null {
  try {
    return fetchRing.filter(
      (r) => r.headerKind === 'rsc' && !r.isPrefetch && r.settleTs === undefined
    ).length;
  } catch (_e) {
    // intentional: sampling must never throw
    return null;
  }
}

/**
 * Count DOM nodes with the "animate-pulse" class — proxy for stale Suspense fallback
 * nodes still visible after URL settle.
 * Fix C: uses `.animate-pulse` class selector (browser hash-map lookup) instead of
 * `[class*="animate-pulse"]` substring attribute selector (full DOM walk).
 * Tailwind generates `.animate-pulse` as a class rule — same nodes targeted, faster.
 * PII audit: count only, no text content or labels read.
 */
function sampleStaleSuspenseFallbackCount(): number | null {
  try {
    if (typeof document === 'undefined') return null;
    return document.querySelectorAll('.animate-pulse').length;
  } catch (_e) {
    // intentional: DOM query failures must not surface from telemetry
    return null;
  }
}

/**
 * Count direct child ELEMENTS of document.body (elements only — excludes the hundreds
 * of React <!--$--> comment nodes that made childNodes.length meaningless as a metric).
 * Uses document.body.children.length (HTMLCollection, elements only).
 * Fix A: called at URL-settle time in recordNavigation; result stored on NavEvent.
 * PII audit: count only, no child content accessed.
 */
function sampleBodyElementCount(): number | null {
  try {
    if (typeof document === 'undefined') return null;
    return document.body.children.length;
  } catch (_e) {
    // intentional: DOM access failures must not surface from telemetry
    return null;
  }
}

/**
 * Count [data-route-skeleton] elements in the DOM.
 *
 * Semantics:
 *  - [data-route-skeleton] is added to the root element of every ROUTE-level loading.tsx
 *    (60 files under src/app/(dashboard)/<route>/loading.tsx).
 *  - The GROUP-level loading.tsx (src/app/(dashboard)/loading.tsx) uses [data-layout-skeleton]
 *    (renamed in WS-A Round 6) and is NOT counted. Before the rename both the group and route
 *    skeleton shared the same attribute, causing a count of 2 on every slow initial /tasks load
 *    (group + route Suspense fallbacks co-mounted simultaneously) and producing 121 false-positive
 *    stacked-ghost events in 7 days.
 *
 * Gate: count >= 2 means two ROUTE subtrees are mid-load simultaneously — a genuine
 * stacked-ghost. A healthy app shows at most ONE route skeleton at a time (the route
 * currently being navigated to). A single slow-loading page therefore returns count === 1
 * and the Stage-2 gate correctly does not fire.
 * count === 0 means no transition in progress — abort, do not fire.
 *
 * Known limitation: a stacked-ghost where the OLD page is FULLY RENDERED (no skeleton
 * showing) is not caught by this signal. Detecting that case would require a per-route-
 * instance DOM marker placed inside a template.tsx remount or per-page attribute — a
 * per-navigation remount behavior change deliberately kept out of scope.
 *
 * Exported for unit testing (Rule #48 regression coverage for the WS-A rename fix).
 * Only called internally by verifyStackedGhost otherwise.
 *
 * PII audit: count only, no element content accessed.
 */
export function sampleRouteSkeletonCount(): number | null {
  try {
    if (typeof document === 'undefined') return null;
    return document.querySelectorAll('[data-route-skeleton]').length;
  } catch (_e) {
    // intentional: DOM query failures must not surface from telemetry
    return null;
  }
}

// ── Stacked-ghost Stage 2 — deferred verify ──────────────────────────────────

/**
 * Stage 2 of the stacked-ghost two-gate capture.
 *
 * Called STACKED_GHOST_CHECK_DELAY_MS (800ms) after every URL settlement.
 * The sole gate is DOM-structural: count [data-route-skeleton] elements.
 *
 *   count === 0: no route transition in progress — abort.
 *   count === 1: exactly one route skeleton — healthy slow page, abort.
 *   count >= 2: two ROUTE subtrees simultaneously mid-load — genuine stacked-ghost, fire.
 *
 * The false-positive root cause (group [data-layout-skeleton] being counted as a route
 * skeleton) was fixed by the WS-A marker rename, not by gating logic here. The former
 * "hydration-aware same-URL gate" was REMOVED: it was dead code after the rename
 * (count-2 on a normal /tasks load dropped to count-1 at the earlier structural check),
 * and the window 'error' hydration signal it relied on is not reliably fired by React's
 * onRecoverableError in production. See the NOTE block above STACKED_GHOST_CHECK_DELAY_MS.
 *
 * WS-B (#418 hydration fix) validation: the native React #418 Sentry issue trend is
 * the reliable signal for that fix — this detector is NOT involved in that validation.
 *
 * Abort condition: if the user navigated to a completely different route pathname since
 * Stage 1 armed, the timer fired on the wrong page — abort silently. Query-string-only
 * changes (same pathname, different search) are NOT aborted; the App Router still issues
 * a navigation for those and could exhibit a genuine 2-skeleton co-mount.
 *
 * PII audit: only numeric counts and sanitizeUrlForTelemetry output.
 *
 * @param expectedUrl  The URL that was settling when Stage 1 armed.
 * @param settleTs     performance.now() from the Stage 1 arm point (for
 *                     calculating ms_since_settle in diagnostics).
 */
function verifyStackedGhost(expectedUrl: string, settleTs: number): void {
  stackedGhostTimer = null;

  // ABORT GATE: if the user navigated to a different route pathname since Stage 1 armed,
  // the timer is stale — fire would be for the wrong page. Compare PATHNAMES only (not
  // full URLs): a query-string change (/tasks?view=list → /tasks) doesn't change which
  // route segment is mounted, so it should NOT abort (it can legitimately co-mount two
  // RSC responses for the query transition). A pathname change (/tasks → /leads) means
  // the timer fired on the wrong page — abort.
  try {
    const expectedPathname = new URL(expectedUrl, window.location.href).pathname;
    if (window.location.pathname !== expectedPathname) {
      return; // Navigated to a different route — timer is stale, abort.
    }
  } catch (_e) {
    // intentional: location/URL access may fail in unusual environments
    return;
  }

  // STRUCTURAL GATE: DOM evidence of co-mounted route subtrees.
  // count === 0: no transition in progress — abort.
  // count === 1: one route skeleton — healthy slow page, abort.
  // count >= 2: two ROUTE skeletons simultaneously visible — genuine stacked-ghost, fire.
  const routeSkeletonCount = sampleRouteSkeletonCount();
  if (routeSkeletonCount === null || routeSkeletonCount < 2) {
    return;
  }

  // Collect supporting diagnostic signals (not used for gate decision).
  const rscInflightAtSettle = sampleRscInflightAtSettle();
  const bodyElementCount = sampleBodyElementCount();

  // Confidence is always 'high' when the structural gate fires — two route-level
  // DOM markers simultaneously present is definitive evidence of co-mount.
  const confidence: PhantomNavConfidence = 'high';

  const fromPathname = sanitizeUrlForTelemetry(expectedUrl);
  const toPathname = fromPathname; // stacked-ghost: URL is correct, page is stuck

  // Route through shouldCapture so dedup applies — the same stuck page should not
  // fire more than DEDUP_LIMIT times in DEDUP_WINDOW_MS.
  if (!shouldCapture('stacked-ghost', fromPathname, toPathname, confidence)) {
    return;
  }

  try {
    Sentry.addBreadcrumb({
      category: 'phantom-nav.stacked-ghost',
      message: `stacked-ghost: ${routeSkeletonCount} route markers after ${STACKED_GHOST_CHECK_DELAY_MS}ms`,
      level: 'warning',
      data: {
        route_skeleton_count: routeSkeletonCount,
        rsc_inflight_at_settle: rscInflightAtSettle,
        body_element_count: bodyElementCount,
        url: fromPathname,
        confidence,
      },
    });

    const msSinceSettle = Math.round(performance.now() - settleTs);

    Sentry.captureMessage('phantom-nav: stacked-ghost', {
      level: 'warning',
      tags: {
        'phantom-nav': 'true',
        'phantom-nav-kind': 'stacked-ghost',
        'phantom-nav-from-pathname': fromPathname,
        'phantom-nav-to-pathname': toPathname,
        'phantom-nav-confidence': confidence,
      },
      contexts: {
        phantom_nav: {
          // URL fields use sanitized form — PII safe.
          actual_url: fromPathname,
          snap_url: fromPathname,
          kind: 'stacked-ghost',
          route_skeleton_count: routeSkeletonCount,
          rsc_inflight_at_settle: rscInflightAtSettle,
          body_element_count: bodyElementCount,
          ms_since_settle: msSinceSettle,
          confidence,
          build_sha: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? null,
        },
      },
      fingerprint: ['phantom-nav', 'stacked-ghost', fromPathname, toPathname],
    });
  } catch (_e) {
    // intentional: telemetry capture must never throw
  }
}

// ── Upgrade 6: confidence scoring ────────────────────────────────────────────

/**
 * Compute a confidence level for a phantom-nav capture.
 * Signals (each worth 1 point):
 *   1. tab_switch_within_snap_window  — the visibilitychange trigger was present
 *   2. inflight RSC/server-action requests at snap time
 *   3. the snap destination URL matches a prior "from" URL in the queue
 *      (direct evidence of the router snapping back to the pre-click page)
 *
 * Score: 3 = high, 2 = medium, ≤1 = low.
 */
function computeConfidence(
  inflightRequests: InFlightRequest[],
  tabSwitchWithinSnapWindow: boolean,
  e0: NavEvent | undefined,
  e1: NavEvent,
  e2: NavEvent
): PhantomNavConfidence {
  let score = 0;

  if (tabSwitchWithinSnapWindow) score++;
  if (inflightRequests.length > 0) score++;
  // Prior URL match: snap destination matches a recorded "from" URL in the queue.
  const snapTo = e2.toUrl;
  const priorFrom = e0?.fromUrl ?? e1.fromUrl;
  if (priorFrom && urlsMatch(snapTo, priorFrom)) score++;

  if (score >= 3) return 'high';
  if (score >= 2) return 'medium';
  return 'low';
}
